import { DexieDownload } from '../db/types'

/**
 * The manifest sections holding downloadable items. `map_pins` and `keypad_numbers` are listed in the
 * same documents but carry no id or version of their own, and nothing is fetched for them.
 */
export const ITEM_TYPES = [
	'collections',
	'screens',
	'media_items',
	'quizzes',
	'maps',
	'forms',
	'themes',
	'badge_groups',
] as const

export type ItemType = (typeof ITEM_TYPES)[number]

export interface ManifestItem {
	id?: number
	version?: number
}

export type VersionedManifest = Partial<Record<ItemType, ManifestItem[]>>

/** item type -> id -> version, the shape a registry row records so a later manifest can be diffed against it. */
export type ItemVersions = Record<string, Record<string, number>>

export interface ManifestItemRef {
	type: ItemType
	id: number
	version: number
}

export const manifestVersions = (manifest: VersionedManifest): ItemVersions => {
	const versions: ItemVersions = {}

	for (const type of ITEM_TYPES) {
		const items = manifest[type]
		if (!items?.length) continue

		const byId: Record<string, number> = {}
		for (const { id, version } of items) {
			if (id !== undefined && version !== undefined) byId[String(id)] = version
		}
		if (Object.keys(byId).length > 0) versions[type] = byId
	}

	return versions
}

export interface VersionDiff {
	/** Republished since the row was built, so their URLs have moved and the new ones are not cached. */
	changed: ManifestItemRef[]
	/** No longer published under this row at all. Their URLs are only worth keeping if another row claims them. */
	removed: ManifestItemRef[]
}

export const staleItems = (row: Pick<DexieDownload, 'versions'>, manifest: VersionedManifest): VersionDiff => {
	const current = manifestVersions(manifest)
	const changed: ManifestItemRef[] = []
	const removed: ManifestItemRef[] = []

	for (const [type, recorded] of Object.entries(row.versions)) {
		const published = current[type] ?? {}

		for (const [id, version] of Object.entries(recorded)) {
			const now = published[id]
			if (now === undefined) removed.push({ type: type as ItemType, id: Number(id), version })
			else if (now !== version) changed.push({ type: type as ItemType, id: Number(id), version: now })
		}
	}

	return { changed, removed }
}

/**
 * The URLs only this row claims. Two tours sharing a media item must both be removed before its audio is,
 * so deleting one leaves the other playable offline.
 */
export const urlsToEvict = (key: string, rows: Pick<DexieDownload, 'key' | 'urls'>[]): string[] => {
	const row = rows.find(candidate => candidate.key === key)
	if (!row) return []

	const claimedElsewhere = new Set(rows.filter(other => other.key !== key).flatMap(other => other.urls))

	return [...new Set(row.urls)].filter(url => !claimedElsewhere.has(url))
}
